import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = '/api';

const AuthContext = createContext(null);

// where to come back to after google sign-in: the page that asked, kept for ten minutes,
// and only ever a path on this site
const RETURN_KEY = 'logos_return_to';

export function rememberReturn() {
  try {
    const path = window.location.pathname + window.location.search;
    if (path !== '/') localStorage.setItem(RETURN_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch (e) {}
}

export function takeReturn() {
  try {
    const saved = JSON.parse(localStorage.getItem(RETURN_KEY) || 'null');
    localStorage.removeItem(RETURN_KEY);
    if (saved && Date.now() - saved.at < 10 * 60 * 1000 && /^\/(?![\/\\])/.test(saved.path)) return saved.path;
  } catch (e) {}
  return '/';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const sessionToken = localStorage.getItem('logos_session_token');
      const headers = {};
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        setUser(await response.json());
      } else {
        localStorage.removeItem('logos_session_token');
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from auth with token in hash, let TokenCallback handle it
    if (window.location.hash?.includes('token=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    rememberReturn();
    // Pass the current origin so the backend knows which domain to redirect back to
    const origin = window.location.origin;
    window.location.href = `${API}/auth/google/login?origin=${encodeURIComponent(origin)}`;
  };

  const logout = async () => {
    try {
      const sessionToken = localStorage.getItem('logos_session_token');
      const headers = {};
      if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
      
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers
      });
    } catch (error) {}
    localStorage.removeItem('logos_session_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
